import React, { useState, useEffect } from "react";
import { Container, Text, Button, Paper, Loader, Alert } from "@mantine/core";
import {
  IconUser,
  IconMail,
  IconClock,
  IconCheck,
  IconX,
  IconLock,
} from "@tabler/icons-react";
import { TeacherProfileProps } from "../../Lib/types";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import { Endpoints } from "../../Lib/endpoints";
import { NonPaginatedResponse } from "../../Lib/responses";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
} from "../../Lib/methods";
import {
  Colors,
  HttpErrorCodes,
  HttpErrorCodesObj,
  HttpSuccessCodesObj,
} from "../../Lib/data";
import "./styles.scss";

const Profile: React.FC = () => {
  const [isRequestingReset, setIsRequestingReset] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const {
    data: profileData,
    isLoading,
    error,
  } = usePerformRequest<NonPaginatedResponse<TeacherProfileProps>>({
    method: Endpoints.GetTeacherProfile().method,
    route: Endpoints.GetTeacherProfile().route,
  });

  const RequestPasswordReset = async () => {
    setIsRequestingReset(true);
    const r = await PerformRequest({
      method: Endpoints.GetRequestPasswordReset().method,
      route: Endpoints.GetRequestPasswordReset().route,
      data: {
        email: profileData?.data?.email,
      },
      callback: () => {
        setIsRequestingReset(false);
      },
    });

    setIsRequestingReset(false);
    if (r && r.status) {
      if (r.status === HttpSuccessCodesObj.Created) {
        DefaultSuccessNotification("Password reset link sent to your email!");
      } else if (r.status === HttpErrorCodesObj.NotFound) {
        DefaultErrorNotification("Account does not exist");
      } else {
        DefaultErrorNotification("Failed to send password reset link");
      }
    }
  };

  if (isLoading) {
    return (
      <Container size="md" className="profile-loading">
        <div className="loading-container">
          <Loader size="xl" color={Colors.PurplePrimary} />
          <Text size="lg" c={Colors.PurplePrimary} mt={16}>
            Loading your profile...
          </Text>
        </div>
      </Container>
    );
  }

  if (error || !profileData?.data) {
    return (
      <Container size="md" className="profile-error">
        <Alert
          icon={<IconX size={16} />}
          title="Error"
          color="red"
          variant="light"
          className="error-alert"
        >
          Failed to load profile. Please try refreshing the page.
        </Alert>
      </Container>
    );
  }

  const profile = profileData.data;

  return (
    <Container size="xl" className="profile-container">
      {showSuccess && (
        <Alert
          icon={<IconCheck size={16} />}
          title="Success!"
          color="green"
          variant="light"
          className="success-alert"
          onClose={() => setShowSuccess(false)}
        >
          Password reset link has been sent to your email address.
        </Alert>
      )}

      <div className="profile-header">
        <div className="profile-avatar">
          <div className="avatar-placeholder">
            {profile.name.charAt(0).toUpperCase()}
          </div>
        </div>
        <div className="profile-title">
          <Text size="2rem" fw={700} c={Colors.PurplePrimary}>
            {profile.name}
          </Text>
          <Text size="lg" c="dimmed">
            Teacher Profile
          </Text>
        </div>
      </div>

      <div className="cards-layout">
        <Paper className="profile-card" shadow="md" radius="lg">
          <div className="profile-section">
            <div className="section-header">
              <IconUser size={20} color={Colors.PurplePrimary} />
              <Text size="lg" fw={600}>
                Personal Information
              </Text>
            </div>

            <div className="field-group">
              <label>Full Name</label>
              <div className="readonly-field">
                <Text size="md">{profile.name}</Text>
              </div>
            </div>

            <div className="field-group">
              <label>Email Address</label>
              <div className="readonly-field">
                <Text size="md">{profile.email}</Text>
              </div>
            </div>

            <div className="field-group">
              <label>Last Login</label>
              <div className="readonly-field">
                <div className="last-login-info">
                  <IconClock size={16} />
                  <Text size="md">
                    {profile.lastLogin
                      ? new Date(profile.lastLogin).toLocaleString()
                      : "Never"}
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </Paper>

        <Paper className="profile-card" shadow="md" radius="lg">
          <div className="profile-section">
            <div className="section-header">
              <IconMail size={20} color={Colors.PurplePrimary} />
              <Text size="lg" fw={600}>
                Account Status
              </Text>
            </div>

            <div className="status-grid">
              <div className="status-item">
                <label>Account Status</label>
                <div
                  className={`status-badge ${
                    profile.isActive ? "active" : "inactive"
                  }`}
                >
                  {profile.isActive ? "Active" : "Inactive"}
                </div>
              </div>

              <div className="status-item">
                <label>Email Verification</label>
                <div
                  className={`status-badge ${
                    profile.isEmailVerified ? "verified" : "unverified"
                  }`}
                >
                  {profile.isEmailVerified ? "Verified" : "Unverified"}
                </div>
              </div>

              <div className="status-item">
                <label>Phone Verification</label>
                <div
                  className={`status-badge ${
                    profile.isPhoneVerified ? "verified" : "unverified"
                  }`}
                >
                  {profile.isPhoneVerified ? "Verified" : "Unverified"}
                </div>
              </div>
            </div>
          </div>
        </Paper>
      </div>

      {/* <Paper className="profile-card" shadow="md" radius="lg">
        <div className="profile-section">
          <div className="section-header">
            <IconLock size={20} color={Colors.PurplePrimary} />
            <Text size="lg" fw={600}>
              Security
            </Text>
          </div>

          <div className="security-info">
            <Text size="sm" c="dimmed" mb={16}>
              Need to change your password? We'll send you a secure link to
              reset it.
            </Text>

            <Button
              onClick={RequestPasswordReset}
              loading={isRequestingReset}
              className="password-reset-btn"
              leftSection={<IconLock size={16} />}
            >
              {isRequestingReset ? "Sending..." : "Request Password Reset"}
            </Button>
          </div>
        </div>
      </Paper> */}
    </Container>
  );
};

export default Profile;
